import type { CustomerConfirmation } from '@/api/client/models/CustomerConfirmation'
import { DocumentationService } from '@/api/client/services/DocumentationService'
import FileManager from '@/classes/manager/FileManager'
import type { TDrawing } from '@/types/drawing'
import { DateTime } from 'luxon'
import { type Sendable } from '@/interfaces/Sendable'
import { useAuthStore } from '@/stores/AuthStore'
import { type Error } from '@/types/error'

type TDehydratedConfirmation = {
  confirmation_data: CustomerConfirmation
  signature: TDrawing | null
  type: 'confirmation'
  meta: {
    transmitted: boolean
    transmitted_at: string
    error_log: Error[]
  }
}

class Confirmation implements Sendable {
  readonly type = 'confirmation'
  meta: {
    transmitted: boolean
    transmitted_at: string
    error_log: Error[]
  }
  confirmation_data: CustomerConfirmation
  signature: TDrawing | null = null
  constructor(
    confirmationData: CustomerConfirmation,
    signature: TDrawing | null = null,
    meta = {
      transmitted: false,
      transmitted_at: '',
      error_log: [] as Error[],
    },
  ) {
    this.confirmation_data = confirmationData
    this.signature = signature
    this.meta = meta
  }

  async send(): Promise<{ error: false | Error }> {
    try {
      if (this.signature && this.signature?.base64.length > 0) {
        const signatureUploadResponse = await FileManager.upload(
          this.signature?.base64,
          this.confirmation_data.employee_id,
        )
        if (!signatureUploadResponse?.data.unassigned_uploaded_file_id) {
          console.error(
            'Confirmation: signature upload failed',
            signatureUploadResponse,
          )
          return {
            error: {
              error_message:
                'signature_upload_failed: ' +
                JSON.stringify(signatureUploadResponse),
              error_code: 'uploadError',
            },
          }
        }
        this.confirmation_data.signature_unassigned_uploaded_file_id =
          signatureUploadResponse.data.unassigned_uploaded_file_id
      }

      this.confirmation_data.sent_at = DateTime.now().toISO()
      console.log('Confirmation: sending confirmation', this.confirmation_data)

      const response = await DocumentationService.storeConfirmation(
        this.confirmation_data,
      )
      console.log('Confirmation sent', response)
      return { error: false }
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        error.status === 401
      )
        await useAuthStore().logout()
      return {
        error: {
          error_message: 'confirmation_send_failed: ' + JSON.stringify(error),
          error_code: 'sendError',
        },
      }
    }
  }
  isReady() {
    return true
  }
  cleanup(): Promise<{ success: boolean }> {
    // there are no files to delete
    return new Promise((resolve) => {
      resolve({ success: true })
    })
  }
  toJSON(): TDehydratedConfirmation {
    return {
      confirmation_data: this.confirmation_data,
      signature: this.signature,
      meta: this.meta,
      type: this.type,
    }
  }
  static fromJSON(
    dehydratedConfirmation: TDehydratedConfirmation,
  ): Confirmation {
    return new Confirmation(
      dehydratedConfirmation.confirmation_data,
      dehydratedConfirmation.signature,
      dehydratedConfirmation.meta,
    )
  }
}
export { Confirmation, type TDehydratedConfirmation }
